'use client';

import React from 'react';
import { Search, Loader2, Truck } from 'lucide-react';
import type { Vehicle } from './useVehicleSearch';

interface VehicleSelectorProps {
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (v: Vehicle | null) => void;
  vehicleSearchQuery: string;
  setVehicleSearchQuery: (q: string) => void;
  showVehicleSearch: boolean;
  setShowVehicleSearch: (show: boolean) => void;
  loadingVehicles: boolean;
  filteredVehicles: Vehicle[];
  vehicleDropdownRef: React.RefObject<HTMLDivElement | null>;
}

export function VehicleSelector({
  selectedVehicle,
  setSelectedVehicle,
  vehicleSearchQuery,
  setVehicleSearchQuery,
  showVehicleSearch,
  setShowVehicleSearch,
  loadingVehicles,
  filteredVehicles,
  vehicleDropdownRef,
}: VehicleSelectorProps) {
  return (
    <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Vehicle *
        </label>
        {selectedVehicle ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-gray-400" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {selectedVehicle.registration || selectedVehicle.title}
                </div>
                {selectedVehicle.fuel_type && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedVehicle.fuel_type}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedVehicle(null);
                setVehicleSearchQuery('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={vehicleDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={vehicleSearchQuery}
                onChange={(e) => {
                  setVehicleSearchQuery(e.target.value);
                  setShowVehicleSearch(true);
                }}
                onFocus={() => setShowVehicleSearch(true)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            {showVehicleSearch && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {loadingVehicles ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                    Loading vehicles...
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {vehicleSearchQuery.trim() ? 'No vehicles found' : 'No vehicles available'}
                  </div>
                ) : (
                  <div>
                    {filteredVehicles.map((vehicle) => (
                      <button
                        key={vehicle.id}
                        type="button"
                        onClick={() => {
                          setSelectedVehicle(vehicle);
                          setShowVehicleSearch(false);
                          setVehicleSearchQuery('');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {vehicle.registration || vehicle.title}
                        </div>
                        {vehicle.fuel_type && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {vehicle.fuel_type}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
